// Die Kolonie — Akt III, die Regeln im Client.
//
// **Wortgleicher Spiegel von `backend/farmining/progression/colony.py`.**
// Solo rechnet der Client, online der Server; dieselben Testfälle laufen auf
// beiden Seiten, und ein Golden Vector hält den Produktionsschritt bitgleich
// (FG-50). Online zeigt der Client nur an: die Raten schreiben den Lagerstand
// zwischen zwei Meldungen fort, gebucht wird beim Server.
//
// Die Funktionen arbeiten auf einem **schmalen Wirt** (`ColonyHost`) statt auf
// dem Spielstand: `state/` liest `sim/`, nicht umgekehrt (FG-05). Was sie vom
// Spielstand brauchen, steht dort — Kolonie, Ära, Ausbau, Vorräte, Statistik.

import { jsRound } from '../core/math';
import {
  balancing, colonyBuildingById, colonyBuildings, colonyRecipeById, colonyRecipes, goodById,
  goods, planetById, planets, projectById, projectsOf,
} from '../data/balancing';
import type { ColonyBuilding, Planet, Project, StatMap } from '../data/types';
import { center, depositAt, inOrbit } from './planets';

/** Eine Anlage auf einer Kachel. Ihr Ort ist zugleich ihr Name. */
export interface ColonyBuildingState {
  kind: string;
  x: number;
  y: number;
  level: number;
  recipe: string;
  /** Der Schalter des Spielers: angehalten verbraucht sie nichts. */
  on: boolean;
}

/** Ein besiedelter Planet. `projects` zählt, die Projekte sind sequenziell. */
export interface Outpost {
  buildings: ColonyBuildingState[];
  projects: number;
}

/** Die zweite Wirtschaft — **neben** `res`, nie darin. */
export interface ColonyState {
  /** Bis wann fortgeschrieben ist — Sekunden seit der Epoche. */
  t: number;
  goods: Record<string, number>;
  reached: Record<string, boolean>;
  planets: Record<string, Outpost>;
}

/**
 * Was der Spieler im Kolonie-Panel gerade tut — **Oberflächenzustand**.
 *
 * Er steht hier und nicht in `ui/`, weil Panel und Planetenszene ihn beide
 * lesen und einander nicht kennen dürfen (FG-04). Er gehört in keinen
 * Spielstand (FG-28): ein Bauplan in der Hand ist kein Fortschritt.
 */
export interface ColonyView {
  planet: string;
  selected: { x: number; y: number } | null;
  /** Bauplan in der Hand — die Anlagenart, die der nächste Tipp setzt. */
  placing: string;
  /** Versetzen: der nächste Tipp setzt die ausgewählte Anlage dorthin. */
  moving: boolean;
}

/** Was die Regeln vom Spielstand lesen und schreiben. */
export interface ColonyHost {
  colony: ColonyState;
  era: number;
  built: number;
  res: Record<string, number>;
  stats: Record<string, number | Record<string, number>>;
}

export function emptyGoods(): Record<string, number> {
  return Object.fromEntries(goods().map((entry) => [entry.id, 0]));
}

export function emptyColony(): ColonyState {
  return { t: 0, goods: emptyGoods(), reached: {}, planets: {} };
}

export const founded = (colony: ColonyState): boolean => Object.keys(colony.planets).length > 0;

/** Die Anlage auf einer Kachel — oder `undefined`. */
export function buildingAt(outpost: Outpost, x: number, y: number): ColonyBuildingState | undefined {
  return outpost.buildings.find((entry) => entry.x === x && entry.y === y);
}

// ── Kosten ───────────────────────────────────────────────────────────────────

const scaledCost = (cost: StatMap, faktor: number): StatMap =>
  Object.fromEntries(Object.entries(cost).map(([gut, menge]) => [gut, jsRound(menge * faktor)]));

/** Was der Schritt von `level` auf `level + 1` kostet — `level` 0 ist das Errichten. */
export function buildingCost(kind: ColonyBuilding, level: number): StatMap {
  return scaledCost(kind.cost, kind.growth ** Math.max(0, level));
}

/** Was die **nächste** Anlage dieser Art auf diesem Planeten kostet. */
export function placeCost(colony: ColonyState, planetId: string, kind: ColonyBuilding): StatMap {
  const outpost = colony.planets[planetId];
  const schon = outpost ? outpost.buildings.filter((entry) => entry.kind === kind.id).length : 0;
  return scaledCost(kind.cost, balancing().colony.placeGrowth ** schon);
}

/** Güter und Schiffsvorräte trennen — sie haben verschiedene Kassen. */
export function splitCost(cost: StatMap): { goods: StatMap; ship: StatMap } {
  const out = { goods: {} as StatMap, ship: {} as StatMap };
  for (const [posten, menge] of Object.entries(cost)) {
    if (goodById(posten)) out.goods[posten] = menge;
    else out.ship[posten] = menge;
  }
  return out;
}

export function canAfford(host: ColonyHost, cost: StatMap): boolean {
  const geteilt = splitCost(cost);
  return Object.entries(geteilt.ship).every(([res, menge]) => (host.res[res] ?? 0) >= menge)
    && Object.entries(geteilt.goods).every(([gut, menge]) => (host.colony.goods[gut] ?? 0) >= menge);
}

/** Der einzige **bezahlte** Weg, auf dem ein Gut sinkt. Prüft vorher. */
export function payGoods(colony: ColonyState, cost: StatMap): boolean {
  if (!Object.entries(cost).every(([gut, menge]) => (colony.goods[gut] ?? 0) >= menge)) return false;
  for (const [gut, menge] of Object.entries(cost)) colony.goods[gut] -= menge;
  return true;
}

// ── Freischaltung ────────────────────────────────────────────────────────────

/** Alle abgeschlossenen Projekte, in Tabellenordnung. */
export function doneProjects(colony: ColonyState): Project[] {
  const out: Project[] = [];
  for (const planet of planets()) {
    const outpost = colony.planets[planet.id];
    if (outpost) out.push(...projectsOf(planet.id).slice(0, outpost.projects));
  }
  return out;
}

export function projectDone(colony: ColonyState, projectId: string): boolean {
  if (!projectId) return true;
  const projekt = projectById(projectId);
  if (!projekt) return false;
  const outpost = colony.planets[projekt.planet];
  if (!outpost) return false;
  return projectsOf(projekt.planet).indexOf(projekt) < outpost.projects;
}

export function nextProject(colony: ColonyState, planetId: string): Project | null {
  const outpost = colony.planets[planetId];
  const reihe = projectsOf(planetId);
  if (!outpost || outpost.projects >= reihe.length) return null;
  return reihe[outpost.projects];
}

/** Ist das letzte Projekt des Akts abgeschlossen? */
export const finished = (colony: ColonyState): boolean =>
  doneProjects(colony).some((entry) => entry.final);

export const buildingUnlocked = (colony: ColonyState, kind: ColonyBuilding): boolean =>
  projectDone(colony, kind.need);

/**
 * Die Bedingung des Akts: das Heimatfeuer brennt — „alles erreicht".
 *
 * Über die Position in der Ausbaukette, wie `builtIds`: die Stufen sind streng
 * sequenziell, also steht das Heimatfeuer genau dann, wenn `built` hinter ihm
 * liegt.
 */
export function homefireBuilt(host: ColonyHost): boolean {
  const index = balancing().builds.findIndex((entry) => entry.id === 'homefire');
  return index >= 0 && host.built > index;
}

/** Der bebaubare Ring um die Kapsel — er wächst mit den Projekten des Planeten. */
export function areaRadius(colony: ColonyState, planetId: string): number {
  const outpost = colony.planets[planetId];
  if (!outpost) return 0;
  let zuwachs = 0;
  for (const projekt of projectsOf(planetId).slice(0, outpost.projects)) zuwachs += projekt.area;
  return balancing().colony.startRadius + zuwachs;
}

export function inArea(colony: ColonyState, planetId: string, x: number, y: number): boolean {
  const c = balancing().colony;
  if (x < 0 || x >= c.gridW || y < 0 || y >= c.gridH) return false;
  const mitte = center();
  const radius = areaRadius(colony, planetId);
  return Math.abs(x - mitte.x) <= radius && Math.abs(y - mitte.y) <= radius;
}

/** Passt die Anlage auf diese Kachel? Fördertürme brauchen ein Vorkommen. */
export function siteOk(planetId: string, kind: ColonyBuilding, x: number, y: number): boolean {
  const vorkommen = depositAt(planetId, x, y);
  const hitze = balancing().colony.depositHeat;
  if (kind.site === 'deposit') return vorkommen !== null && vorkommen.kind !== hitze;
  if (kind.site === hitze) return vorkommen !== null && vorkommen.kind === hitze;
  return true;
}

/**
 * Warum hier (noch) nicht gelandet werden kann — leer heißt: es geht.
 *
 * Wortgleich zu `land_locked`: die Begründung kommt aus **derselben**
 * Auswertung wie die Sperre (`oberflaeche.md`).
 */
export function landLocked(host: ColonyHost, planet: Planet): string {
  const colony = host.colony;
  if (colony.planets[planet.id]) return `Auf ${planet.name} steht schon eine Kapsel.`;
  if (host.era < balancing().colony.minEra) return 'Die Planeten liegen im Heimatsystem.';
  if (!homefireBuilt(host)) return 'Erst muss das Heimatfeuer brennen.';
  if (planet.need && !projectDone(colony, planet.need)) {
    const vorher = projectById(planet.need);
    const ort = vorher ? planetById(vorher.planet) : undefined;
    return `Erst braucht es ${vorher?.name ?? planet.need} auf ${ort?.name ?? '?'}.`;
  }
  if (!colony.reached[planet.id]) return `Flieg erst in die Umlaufbahn von ${planet.name}.`;
  return '';
}

/** Warum diese Anlage hier nicht stehen kann — leer heißt: sie kann. */
export function placeLocked(
  host: ColonyHost, planetId: string, kind: ColonyBuilding, x: number, y: number,
): string {
  const colony = host.colony;
  const outpost = colony.planets[planetId];
  if (!outpost) return 'Hier ist noch niemand gelandet.';
  if (kind.role === 'hq') return 'Die Kapsel setzt die Landung, nicht der Bauplan.';
  if (!buildingUnlocked(colony, kind)) {
    return `${kind.name} braucht erst ${projectById(kind.need)?.name ?? kind.need}.`;
  }
  if (!inArea(colony, planetId, x, y)) return 'Diese Kachel liegt außerhalb des erschlossenen Gebiets.';
  if (buildingAt(outpost, x, y)) return 'Hier steht schon etwas.';
  if (!siteOk(planetId, kind, x, y)) {
    if (kind.site === balancing().colony.depositHeat) {
      return `${kind.name} braucht eine ${balancing().colony.depositHeatName}.`;
    }
    return `${kind.name} braucht ein Rohstoffvorkommen.`;
  }
  return '';
}

// ── Produktion ───────────────────────────────────────────────────────────────

/** Anlagenart -> Zuschlag aus allen abgeschlossenen Projekten (additiv). */
export function boosts(colony: ColonyState): StatMap {
  const out: StatMap = {};
  for (const projekt of doneProjects(colony)) {
    for (const [art, zuschlag] of Object.entries(projekt.boost)) out[art] = (out[art] ?? 0) + zuschlag;
  }
  return out;
}

/** Die Schiffsboni der abgeschlossenen Projekte — für `derivedStats`. */
export function projectPerks(colony: ColonyState): StatMap[] {
  return doneProjects(colony).filter((entry) => Object.keys(entry.perk).length > 0)
    .map((entry) => entry.perk);
}

/** Die summierte Leistung aller **eingeschalteten** Anlagen, ohne Zuschlag. */
export interface Capacity {
  extract: StatMap;
  recipe: StatMap;
  power: StatMap;
  demand: number;
}

/**
 * Summiert in Tabellenordnung der Planeten und in Listenordnung der Anlagen —
 * genau wie die Python-Seite, sonst laufen die Fließkommasummen auseinander.
 */
export function capacity(colony: ColonyState): Capacity {
  const cap: Capacity = { extract: {}, recipe: {}, power: {}, demand: 0 };
  for (const planet of planets()) {
    const outpost = colony.planets[planet.id];
    if (!outpost) continue;
    for (const anlage of outpost.buildings) {
      if (anlage.on) count(cap, planet.id, anlage);
    }
  }
  return cap;
}

function count(cap: Capacity, planetId: string, anlage: ColonyBuildingState): void {
  const kind = colonyBuildingById(anlage.kind);
  if (!kind) return;
  let stufe = anlage.level;
  const vorkommen = kind.site ? depositAt(planetId, anlage.x, anlage.y) : null;
  if (kind.site) stufe *= vorkommen ? balancing().colony.richness[vorkommen.rich - 1] : 0;
  if (kind.role === 'drill') {
    if (vorkommen) cap.extract[vorkommen.kind] = (cap.extract[vorkommen.kind] ?? 0) + stufe;
  } else if (kind.role === 'make' && anlage.recipe) {
    cap.recipe[anlage.recipe] = (cap.recipe[anlage.recipe] ?? 0) + stufe;
  }
  if (kind.power > 0) cap.power[kind.id] = (cap.power[kind.id] ?? 0) + stufe;
  else if (kind.power < 0) cap.demand += -kind.power * anlage.level;
}

/** Was eine Zeitscheibe bewegt hat — oder je Sekunde bewegen würde. */
export interface Flow {
  made: StatMap;
  used: StatMap;
  supply: number;
  demand: number;
  /** Wie viel vom Bedarf das Netz deckt, 0…1. */
  ratio: number;
  /** Rezepte, die nicht voll liefen, weil ein Eingang fehlte. */
  starved: Record<string, boolean>;
}

function book(card: StatMap, gut: string, menge: number): void {
  if (menge > 0) card[gut] = (card[gut] ?? 0) + menge;
}

function power(colony: ColonyState, cap: Capacity, boost: StatMap, dt: number, flow: Flow): void {
  let angebot = 0;
  for (const kind of colonyBuildings()) {
    const stufen = cap.power[kind.id] ?? 0;
    if (stufen <= 0) continue;
    let anteil = 1;
    if (kind.fuel) {
      const bedarf = stufen * kind.fuelRate * dt;
      const vorrat = colony.goods[kind.fuel] ?? 0;
      anteil = bedarf > 0 ? Math.min(1, vorrat / bedarf) : 0;
      const verbrauch = bedarf * anteil;
      colony.goods[kind.fuel] = Math.max(0, vorrat - verbrauch);
      book(flow.used, kind.fuel, verbrauch);
    }
    angebot += stufen * kind.power * (1 + (boost[kind.id] ?? 0)) * anteil;
  }
  flow.supply = angebot;
  flow.demand = cap.demand;
  flow.ratio = cap.demand <= 0 ? 1 : Math.min(1, angebot / cap.demand);
}

/**
 * Eine Zeitscheibe: Netz, Förderung, dann jedes Rezept in Tabellenordnung.
 *
 * **Die Reihenfolge ist Vertrag** (FG-50) — wortgleich zu `_produce`.
 */
export function produce(
  colony: ColonyState, dt: number, cap: Capacity = capacity(colony), boost: StatMap = boosts(colony),
): Flow {
  const flow: Flow = { made: {}, used: {}, supply: 0, demand: 0, ratio: 1, starved: {} };
  power(colony, cap, boost, dt, flow);
  const p = flow.ratio;

  const turm = 1 + (boost.drill ?? 0);
  for (const rohstoff of goods()) {
    const stufen = cap.extract[rohstoff.id] ?? 0;
    if (stufen <= 0 || rohstoff.extract <= 0) continue;
    const menge = rohstoff.extract * stufen * turm * p * dt;
    colony.goods[rohstoff.id] += menge;
    book(flow.made, rohstoff.id, menge);
  }

  for (const rezept of colonyRecipes()) {
    const stufen = cap.recipe[rezept.id] ?? 0;
    if (stufen <= 0) continue;
    const laeufe = stufen * (1 + (boost[rezept.building] ?? 0)) * p * dt;
    let anteil = 1;
    for (const [gut, menge] of Object.entries(rezept.inputs)) {
      const bedarf = menge * laeufe;
      if (bedarf > 0) anteil = Math.min(anteil, (colony.goods[gut] ?? 0) / bedarf);
    }
    if (anteil < 1) flow.starved[rezept.id] = true;
    if (anteil <= 0) continue;
    for (const [gut, menge] of Object.entries(rezept.inputs)) {
      const verbrauch = menge * laeufe * anteil;
      colony.goods[gut] = Math.max(0, colony.goods[gut] - verbrauch);
      book(flow.used, gut, verbrauch);
    }
    for (const [gut, menge] of Object.entries(rezept.outputs)) {
      const ertrag = menge * laeufe * anteil;
      colony.goods[gut] += ertrag;
      book(flow.made, gut, ertrag);
    }
  }
  return flow;
}

function bump(host: ColonyHost, counter: string, key: string, amount: number): void {
  if (amount <= 0) return;
  const card = host.stats[counter];
  if (typeof card !== 'object') return;
  card[key] = (card[key] ?? 0) + amount;
}

/**
 * Bis `now` fortschreiben — **gedeckelt**. Gibt die gutgeschriebene Zeit zurück.
 *
 * Die Kolonie arbeitet, solange gespielt wird: nie mehr als `gapMax` Sekunden
 * am Stück. Wortgleich zu `advance` — idempotent durch Bauweise.
 */
export function advance(host: ColonyHost, now: number): number {
  const colony = host.colony;
  if (!founded(colony)) return 0;
  if (colony.t <= 0) { colony.t = now; return 0; }
  if (now <= colony.t) return 0;
  const spanne = Math.min(now - colony.t, balancing().colony.gapMax);
  colony.t = now;
  const cap = capacity(colony);
  const boost = boosts(colony);
  let rest = spanne;
  while (rest > 1e-9) {
    const scheibe = Math.min(rest, balancing().colony.step);
    const flow = produce(colony, scheibe, cap, boost);
    for (const [gut, menge] of Object.entries(flow.made)) bump(host, 'goodsMade', gut, menge);
    rest -= scheibe;
  }
  host.stats.colonyTime = ((host.stats.colonyTime as number) ?? 0) + spanne;
  return spanne;
}

/** Was die Kolonie **je Sekunde** bewegt — gerechnet auf einer Kopie, ohne Folgen. */
export function rates(colony: ColonyState): Flow {
  const kopie: ColonyState = { ...colony, goods: { ...colony.goods } };
  return produce(kopie, 1);
}

/** Was eine einzelne Anlage je Sekunde bewegt — bei vollem Eingang, zur Anzeige. */
export interface BuildingFlow {
  made: StatMap;
  used: StatMap;
  /** Energie: positiv erzeugt, negativ verbraucht. */
  power: number;
}

/**
 * Die Leistung **einer** Anlage, wie sie im Auswahlfeld steht.
 *
 * Dieselben Faktoren wie im Schritt — Stufe, Ergiebigkeit, Zuschlag,
 * Deckungsgrad des Netzes —, aber ohne Vorrat: ob ein Eingang fehlt, sagt der
 * Schritt (`Flow.starved`), nicht diese Rechnung. Gebucht wird hier nichts.
 */
export function buildingFlow(
  planetId: string, anlage: ColonyBuildingState, boost: StatMap, ratio: number,
): BuildingFlow {
  const out: BuildingFlow = { made: {}, used: {}, power: 0 };
  const kind = colonyBuildingById(anlage.kind);
  if (!kind || !anlage.on) return out;
  const vorkommen = kind.site ? depositAt(planetId, anlage.x, anlage.y) : null;
  const reich = vorkommen ? balancing().colony.richness[vorkommen.rich - 1] : 0;
  const stufe = anlage.level * (kind.site ? reich : 1);
  if (kind.power > 0) {
    out.power = stufe * kind.power * (1 + (boost[kind.id] ?? 0));
    if (kind.fuel) out.used[kind.fuel] = stufe * kind.fuelRate;
  } else {
    out.power = kind.power * anlage.level;
  }
  if (kind.role === 'drill' && vorkommen) {
    const gut = goodById(vorkommen.kind);
    if (gut) out.made[gut.id] = gut.extract * stufe * (1 + (boost.drill ?? 0)) * ratio;
  } else if (kind.role === 'make') {
    const rezept = colonyRecipeById(anlage.recipe);
    if (rezept) {
      const laeufe = stufe * (1 + (boost[kind.id] ?? 0)) * ratio;
      for (const [gut, menge] of Object.entries(rezept.inputs)) out.used[gut] = menge * laeufe;
      for (const [gut, menge] of Object.entries(rezept.outputs)) out.made[gut] = menge * laeufe;
    }
  }
  return out;
}

/**
 * Der Lagerstand **zur Anzeige**, fortgeschrieben um `elapsed` Sekunden.
 *
 * Reine Anzeige: online kommt der Stand im Takt des Servers, solo einmal je
 * Sekunde — dazwischen wäre jede Zahl eine Treppe. Geschrieben wird in einen
 * übergebenen Puffer (FG-14), gebucht wird nichts.
 */
export function shownGoods(
  colony: ColonyState, flow: Flow, elapsed: number, out: Record<string, number>,
): Record<string, number> {
  for (const gut of goods()) {
    const netto = (flow.made[gut.id] ?? 0) - (flow.used[gut.id] ?? 0);
    out[gut.id] = Math.max(0, (colony.goods[gut.id] ?? 0) + netto * Math.max(0, elapsed));
  }
  return out;
}

// ── Die Umlaufbahn ───────────────────────────────────────────────────────────

/** Planeten abhaken, in deren Umlaufbahn das Schiff steht. Solo; online tut es der Server. */
export function markReached(host: ColonyHost, x: number, y: number, seed: number): string[] {
  if (host.era < balancing().colony.minEra) return [];
  const neu: string[] = [];
  for (const planet of planets()) {
    if (host.colony.reached[planet.id]) continue;
    if (inOrbit(x, y, planet, seed, host.era)) {
      host.colony.reached[planet.id] = true;
      neu.push(planet.id);
    }
  }
  return neu;
}

// ── Laden ────────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && isFinite(value) ? value : fallback;
}

/** Eine Anlage aus Fremdinhalt — oder `null`, wenn sie so nicht stehen kann. */
function readBuilding(raw: unknown, planetId: string): ColonyBuildingState | null {
  if (!isRecord(raw) || typeof raw.kind !== 'string') return null;
  const art = colonyBuildingById(raw.kind);
  if (!art) return null;
  const c = balancing().colony;
  const x = Math.trunc(num(raw.x, -1));
  const y = Math.trunc(num(raw.y, -1));
  if (x < 0 || x >= c.gridW || y < 0 || y >= c.gridH) return null;
  const mitte = center();
  if ((art.role === 'hq') !== (x === mitte.x && y === mitte.y)) return null;
  const vorkommen = depositAt(planetId, x, y);
  if (art.site === 'deposit' && (!vorkommen || vorkommen.kind === c.depositHeat)) return null;
  if (art.site === c.depositHeat && (!vorkommen || vorkommen.kind !== c.depositHeat)) return null;
  const recipe = art.recipes.length === 0 ? ''
    : typeof raw.recipe === 'string' && art.recipes.includes(raw.recipe) ? raw.recipe
      : art.recipes[0];
  return {
    kind: art.id, x, y,
    level: Math.trunc(Math.max(1, Math.min(art.maxLevel, num(raw.level, 1)))),
    recipe,
    on: raw.on !== false,
  };
}

function readOutpost(raw: unknown, planetId: string): Outpost {
  const data = isRecord(raw) ? raw : {};
  const gesehen = new Set<string>();
  const buildings: ColonyBuildingState[] = [];
  for (const entry of Array.isArray(data.buildings) ? data.buildings : []) {
    const anlage = readBuilding(entry, planetId);
    if (!anlage || gesehen.has(`${anlage.x}:${anlage.y}`)) continue;
    gesehen.add(`${anlage.x}:${anlage.y}`);
    buildings.push(anlage);
  }
  const reihe = projectsOf(planetId).length;
  return { buildings, projects: Math.trunc(Math.max(0, Math.min(reihe, num(data.projects)))) };
}

/**
 * Die Kolonie aus Fremdinhalt — wortgleich zu `_colony`. Fehlt sie, gibt es
 * noch keine; ein Zeitpunkt in der Zukunft gilt als „jetzt" (FG-27).
 */
export function readColony(raw: unknown, now: number): ColonyState {
  const out = emptyColony();
  if (!isRecord(raw)) return out;
  if (isRecord(raw.goods)) {
    for (const [key, value] of Object.entries(raw.goods)) {
      if (key in out.goods) out.goods[key] = Math.max(0, num(value));
    }
  }
  if (isRecord(raw.reached)) {
    for (const [id, value] of Object.entries(raw.reached)) {
      if (value && planetById(id)) out.reached[id] = true;
    }
  }
  if (isRecord(raw.planets)) {
    const roh = raw.planets;
    for (const planet of planets()) {
      if (planet.id in roh) out.planets[planet.id] = readOutpost(roh[planet.id], planet.id);
    }
  }
  out.t = Math.min(Math.max(0, num(raw.t)), now);
  return out;
}

/** Die Rezepte einer Anlage, als Tabellenzeilen. */
export const recipesOf = (kind: ColonyBuilding): ReturnType<typeof colonyRecipeById>[] =>
  kind.recipes.map((id) => colonyRecipeById(id));
